import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  WebPushSubscription,
  NotificationService,
} from '../notificationService';
import { getEnvironmentVariable } from '../../helper/config';
import { PushNotificationService } from '../pushNotificationService';
import {
  PushSubscription,
  GetPushSubscriptionRequest,
  GetPushSubscriptionResponse,
} from '@inplay/models';

interface NotificationContextValue {
  subscription: WebPushSubscription | null;
  isActiveSubscription: boolean;
}

const NotificationContext = createContext<NotificationContextValue>({
  subscription: null,
  isActiveSubscription: false,
});

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [subscription, setSubscription] = useState<WebPushSubscription | null>(
    null,
  );
  const [isActiveSubscription, setIsActiveSubscription] =
    useState<boolean>(false);

  const value = useMemo<NotificationContextValue>(
    () => ({
      subscription,
      isActiveSubscription,
    }),
    [subscription, isActiveSubscription],
  );

  useEffect(() => {
    const init = async () => {
      console.log('[NotificationProvider] Initializing NotificationService');
      const notificationService = new NotificationService({
        vapidPublicKey: getEnvironmentVariable('REACT_APP_VAPID_PUBLIC_KEY'),
      });
      const pushNotificationService = new PushNotificationService();

      const getSubscriptionRequest: GetPushSubscriptionRequest = {
        deviceId: localStorage.getItem('deviceId') ?? 'deviceId',
      };

      let pushSubscription: PushSubscription | null = null;
      const getSubscriptionResponse: GetPushSubscriptionResponse =
        await pushNotificationService.getPushSubscription(
          getSubscriptionRequest,
        );

      if (
        getSubscriptionResponse.pushSubscriptions &&
        getSubscriptionResponse.pushSubscriptions.length > 0
      ) {
        pushSubscription = getSubscriptionResponse.pushSubscriptions[0];
        setIsActiveSubscription(true);
        console.log(
          '[NotificationProvider] Using an active subscription for current device',
          { deviceId: getSubscriptionRequest.deviceId, pushSubscription },
        );
      }

      const webPushSubscription =
        await notificationService.subscribeToPush(pushSubscription);
      setSubscription(webPushSubscription);
      console.log(
        '[NotificationProvider] Subscribing to Web Push Notification',
        { webPushSubscription },
      );
    };

    init();
  }, []);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationService = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (!context)
    throw new Error(
      'useNotificationService must be used inside NotificationProvider',
    );
  return context;
};
